import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Col, Form, Icon, Input, Modal, notification, Row, Typography } from 'antd';

import { loginUser } from '../../redux/auth';
import {
  passwordRecoveryRequest,
  passwordRecoveryReset,
  passwordRecoveryVerify,
} from '../../services/api';

const WARNING_STORAGE_KEY = 'preauth_warning_ack'; // session-based ack

const Login = ({ form, loginUser }) => {
  const { getFieldDecorator, validateFields, resetFields, setFields } = form;

  // предупреждение
  const [state, setState] = useState(() => {
    return { warningOpen: true };
  });
  const { warningOpen } = state;

  // восстановление
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [serverToken, setServerToken] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const isCounting = secondsLeft > 0;

  // iframe cleaner (как у тебя)
  useEffect(() => {
    (function () {
      const isBad = (el) =>
        el &&
        el.tagName === 'IFRAME' &&
        /position\s*:\s*fixed/i.test(el.getAttribute('style') || '') &&
        /2147483647/.test(el.getAttribute('style') || '');

      const mo = new MutationObserver((muts) => {
        for (const m of muts) {
          for (const n of m.addedNodes) {
            if (n.nodeType === 1) {
              if (isBad(n)) {
                n.remove();
                // eslint-disable-next-line no-console
                console.log('Observer removed bad iframe (added):', n.src || '(no src)');
              }
              n.querySelectorAll?.('iframe').forEach((ifr) => {
                if (isBad(ifr)) {
                  ifr.remove();
                  // eslint-disable-next-line no-console
                  console.log('Observer removed nested bad iframe:', ifr.src || '(no src)');
                }
              });
            }
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
      // eslint-disable-next-line no-console
      console.log('Observer armed');
    })();
  }, []);

  // таймер 60с
  useEffect(() => {
    if (!isCounting) return;
    const t = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [isCounting]);

  // Валидация пароля
  // const validatePassword = useCallback((rule, value, callback) => {
  //   if (!value) return callback();
  //   const hasLen = value.length >= 8;
  //   const hasLower = /[a-z]/.test(value);
  //   const hasUpper = /[A-Z]/.test(value);
  //   const hasDigit = /\d/.test(value);
  //   const hasSpecial = /[#@$%^&*()_\-+=[\]{};':"\\|,.<>/?~]/.test(value);
  //   if (hasLen && hasLower && hasUpper && hasDigit && hasSpecial) return callback();
  //   return callback(
  //     'Пароль должен быть ≥ 8 символов и содержать строчные и прописные буквы, цифры и спецсимволы (#, $, @, …).'
  //   );
  // }, []);

  const handleAcknowledge = useCallback((e) => {
    setState({ warningOpen: false });
  }, []);
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      validateFields(['username', 'password'], (err, values) => {
        if (!err) loginUser(values);
      });
    },
    [validateFields, loginUser]
  );

  // запрос кода на email
  const handleGetCode = async () => {
    validateFields(['recoveryEmail'], async (err, values) => {
      if (err) return;
      const email = (values.recoveryEmail || '').trim();
      setRequestLoading(true);
      try {
        const res = await passwordRecoveryRequest(email);
        if (!res || !res.token) {
          notification.error({
            message: 'Не удалось отправить код',
            description: 'Сервер не вернул токен подтверждения.',
          });
          return;
        }
        setServerToken(res.token);
        setSecondsLeft(60);
        setRequestSuccess(true);
        notification.success({
          message: 'Код отправлен',
          description: 'На вашу почту был отправлен код подтверждения.',
        });
      } catch (e) {
        notification.error({
          message: 'Ошибка отправки кода',
          description:
            e?.message || 'По указанному электронной почте нет зарегистрированного пользователя',
        });
      } finally {
        setRequestLoading(false);
      }
    });
  };

  // проверить код
  const verifyCode = async () => {
    const code = form.getFieldValue('recoveryCode');
    if (!serverToken || !code) return false;
    try {
      setVerifyLoading(true);
      await passwordRecoveryVerify({ token: serverToken, code });
      return true;
    } catch (e) {
      setFields([{ name: 'recoveryCode', errors: ['Неверный код'] }]);
      notification.error({
        message: 'Неверный код подтверждения',
        description: e?.message || 'Проверьте введённый код и попробуйте ещё раз.',
      });
      return false;
    } finally {
      setVerifyLoading(false);
    }
  };

  // сохранить новый пароль
  const handleSaveNewPassword = async () => {
    validateFields(
      ['recoveryEmail', 'recoveryCode', 'newPassword', 'newPasswordConfirm'],
      async (err, values) => {
        if (err) return;
        const p1 = values.newPassword;
        const p2 = values.newPasswordConfirm;
        if (p1 !== p2) {
          const msg = "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match";
          setFields([
            { name: 'newPassword', errors: [msg] },
            { name: 'newPasswordConfirm', errors: [msg] },
          ]);
          return;
        }

        const ok = await verifyCode();
        if (!ok) return;

        setSaveLoading(true);
        try {
          await passwordRecoveryReset({ token: serverToken, newPassword: p1 });
          Modal.success({
            title: 'Пароль изменён',
            content: 'Вы можете войти, используя новый пароль.',
            onOk: () => window.location.replace('/signin'),
          });
          // сброс state восстановления
          setRecoveryOpen(false);
          setServerToken('');
          setSecondsLeft(0);
          setRequestSuccess(false);
          resetFields(['recoveryEmail', 'recoveryCode', 'newPassword', 'newPasswordConfirm']);
        } catch (e) {
          notification.error({
            message: 'Ошибка сохранения',
            description: e?.message || 'Не удалось сбросить пароль. Попробуйте позже.',
          });
        } finally {
          setSaveLoading(false);
        }
      }
    );
  };

  return (
    <>
      <Modal
        visible={warningOpen}
        destroyOnClose
        getContainer={false}
        closable={false}
        maskClosable={false}
        centered
        wrapClassName="white-modal"
        bodyStyle={{ background: '#fff' }}
        title={
          <span>
            <Icon type="exclamation-circle" style={{ marginRight: 8, color: '#faad14' }} />
            Предупреждение
          </span>
        }
        footer={[
          <Button key="ok" type="primary" htmlType="button" onClick={handleAcknowledge}>
            Согласен
          </Button>,
        ]}
      >
        <Typography.Paragraph style={{ marginBottom: 0, color: '#000' }}>
          Неправомерное использование учетных данных третьего лица при регистрации и авторизации в
          системе влечет ответственность в соответствии с законодательством РК.
        </Typography.Paragraph>
      </Modal>

      <Row style={{ paddingTop: 200 }}>
        <Col span={6} offset={9}>
          <h2 style={{ textAlign: 'center' }}>
            {recoveryOpen ? 'Восстановление пароля' : 'Авторизация'}
          </h2>

          {/* ОДИН Form с Form.create, внутри — либо логин, либо восстановление */}
          <Form onSubmit={handleSubmit} className="login-form" autoComplete="off">
            {!recoveryOpen && (
              <>
                <Form.Item hasFeedback>
                  {getFieldDecorator('username', {
                    validateTrigger: ['onBlur', 'onSubmit'],
                    rules: [{ required: true, message: 'Пожалуйста, введите логин!' }],
                  })(
                    <Input
                      prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />}
                      placeholder="Логин"
                      size="large"
                      autoComplete="username"
                    />
                  )}
                </Form.Item>

                <Form.Item hasFeedback>
                  {getFieldDecorator('password', {
                    validateFirst: true,
                    validateTrigger: ['onBlur', 'onSubmit'],
                    rules: [
                      { required: true, whitespace: true, message: 'Пожалуйста, введите пароль!' },
                      // { validator: validatePassword },
                    ],
                  })(
                    <Input
                      prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                      type="password"
                      placeholder="Не менее 8 символов"
                      size="large"
                      autoComplete="current-password"
                    />
                  )}
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                  size="large"
                  block
                >
                  Войти
                </Button>
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <Button
                    type="link"
                    htmlType="button"
                    onClick={() => {
                      setRecoveryOpen(true);
                      setServerToken('');
                      setSecondsLeft(0);
                      setRequestSuccess(false);
                      resetFields([
                        'recoveryEmail',
                        'recoveryCode',
                        'newPassword',
                        'newPasswordConfirm',
                      ]);
                    }}
                  >
                    Забыли пароль?
                  </Button>
                </div>
              </>
            )}

            {recoveryOpen && (
              <div style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="button"
                  style={{ marginBottom: 16 }}
                  onClick={() => {
                    setRecoveryOpen(false);
                    setServerToken('');
                    setSecondsLeft(0);
                    setRequestSuccess(false);
                    resetFields([
                      'recoveryEmail',
                      'recoveryCode',
                      'newPassword',
                      'newPasswordConfirm',
                    ]);
                  }}
                >
                  <Icon type="arrow-left" style={{ marginRight: 8 }} />
                  Назад к входу
                </Button>

                <Form.Item hasFeedback>
                  {getFieldDecorator('recoveryEmail', {
                    rules: [
                      { required: true, message: 'Данное поле должно быть заполнено' },
                      { type: 'email', message: 'Некорректный e-mail' },
                    ],
                  })(
                    <Input
                      size="large"
                      placeholder="Email для кода подтверждения"
                      autoComplete="email"
                      prefix={<Icon type="mail" style={{ color: 'rgba(0,0,0,.25)' }} />}
                    />
                  )}
                </Form.Item>

                {/* Кнопки под Email — получить/повторно + таймер */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <Button
                    htmlType="button"
                    onClick={handleGetCode}
                    disabled={isCounting || requestLoading}
                    loading={requestLoading}
                  >
                    {isCounting ? 'Код отправлен' : 'Получить код подтверждения'}
                  </Button>

                  {isCounting ? (
                    <Typography.Text type="primary" style={{ color: 'white' }}>
                      Введите код за {secondsLeft} сек
                    </Typography.Text>
                  ) : (
                    <Button
                      htmlType="button"
                      onClick={handleGetCode}
                      disabled={!form.getFieldValue('recoveryEmail')}
                    >
                      <Icon type="reload" />
                      Отправить код повторно
                    </Button>
                  )}
                </div>

                {requestSuccess && (
                  <Alert
                    type="success"
                    showIcon
                    message="На вашу почту был отправлен код подтверждения."
                    style={{ marginBottom: 16 }}
                  />
                )}

                {serverToken && (
                  <>
                    <Form.Item label="Введите код из письма" hasFeedback>
                      {getFieldDecorator('recoveryCode', {
                        rules: [
                          { required: true, message: 'Данное поле должно быть заполнено' },
                          { len: 4, message: 'Код состоит из 4 цифр' },
                        ],
                      })(
                        <Input
                          size="large"
                          maxLength={4}
                          placeholder="4-значный код"
                          prefix={<Icon type="number" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        />
                      )}
                    </Form.Item>

                    <Form.Item label="Новый пароль" hasFeedback>
                      {getFieldDecorator('newPassword', {
                        rules: [
                          { required: true, message: 'Данное поле должно быть заполнено' },
                          // { validator: validatePassword },
                        ],
                      })(
                        <Input
                          type="password"
                          size="large"
                          placeholder="Не менее 8 символов"
                          autoComplete="new-password"
                          prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        />
                      )}
                    </Form.Item>

                    <Form.Item label="Повторите пароль" hasFeedback>
                      {getFieldDecorator('newPasswordConfirm', {
                        rules: [
                          { required: true, message: 'Данное поле должно быть заполнено' },
                          {
                            validator: (_, value, cb) => {
                              const p1 = form.getFieldValue('newPassword');
                              if (!value || p1 === value) return cb();
                              return cb(
                                "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match"
                              );
                            },
                          },
                        ],
                      })(
                        <Input
                          type="password"
                          size="large"
                          placeholder="Повторите пароль"
                          autoComplete="new-password"
                          prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        />
                      )}
                    </Form.Item>

                    <Button
                      type="primary"
                      htmlType="button"
                      block
                      onClick={handleSaveNewPassword}
                      loading={verifyLoading || saveLoading}
                    >
                      Сохранить
                    </Button>
                  </>
                )}
              </div>
            )}
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default connect(null, { loginUser })(Form.create()(Login));
